import React from 'react';
import StringConditionColorDescription from '@components/ColorPicker/StringConditionColorDescription';
import { AvTooltip, ColorItem, DefaultColors, Filter, NumberConditionType } from '@fabric-ui/components';
import { FormatterType, MeasurementUnit } from '@fabric-ui/components/Table';
import { flex } from '@fabric-ui/theme';
import { ellipsis, generateOption } from '@fabric-ui/utils';
import { Box, Theme } from '@mui/material';
import AvItemWithLogoGroup, { IconSize } from '../../components/AvItemWithLogoGroup';
import ColorDescription from '../../components/ColorPicker/ColorDescription';
import { IconVariant } from '../../components/ItemWithLogo';
import { generateUUID } from '../../utils/utils';
import { AllTypeWidgets, Data, Field, WidgetCategory } from '../CustomDashboards/types';
import {
  DimensionFormattingRule,
  FormattingCondition,
  FormattingRule,
  FormattingRuleType,
  NumericFilter,
  NumericFormattingCondition,
  NumericFormattingRule,
  StringFilter,
} from './types';

export const NONE_ID = 'none';

export const defaultFormattingCondition = (): NumericFormattingCondition => ({
  id: generateUUID(),
  color: DefaultColors[4], // primary[500] - '#0072CEFF'
  filter: {
    expression: {
      fieldName: '',
      numberCondition: {
        gte: 0,
      },
    },
  },
});

export const defaultFormattingCondition1 = (): NumericFormattingCondition => ({
  id: generateUUID(),
  color: DefaultColors[0], // negative[500] - '#FF3D3DFF'
  filter: {
    expression: {
      fieldName: '',
      numberCondition: {
        lte: 25,
      },
    },
  },
});
const defaultFormattingCondition2 = (): NumericFormattingCondition => ({
  id: generateUUID(),
  color: DefaultColors[1], // orange[500] - '#FFA600FF'
  filter: {
    expression: {
      fieldName: '',
      numberCondition: {
        between: {
          from: 26,
          to: 50,
        },
      },
    },
  },
});

const defaultFormattingCondition3 = (): NumericFormattingCondition => ({
  id: generateUUID(),
  color: DefaultColors[2], // yellow[500] - '#FFD500FF'
  filter: {
    expression: {
      fieldName: '',
      numberCondition: {
        between: {
          from: 51,
          to: 75,
        },
      },
    },
  },
});

const defaultFormattingCondition4 = (): NumericFormattingCondition => ({
  id: generateUUID(),
  color: DefaultColors[3], // positive[500] - '#00E5FFFF'
  filter: {
    expression: {
      fieldName: '',
      numberCondition: {
        gte: 76,
      },
    },
  },
});

export const defaultFormattingConditionFallback = (): FormattingCondition => ({
  id: generateUUID(),
  color: DefaultColors[4], // primary[500] - '#0072CEFF'
});

export const defaultFormattingConditions = (): NumericFormattingCondition[] => [
  defaultFormattingCondition1(),
  defaultFormattingCondition2(),
  defaultFormattingCondition3(),
  defaultFormattingCondition4(),
  defaultFormattingConditionFallback() as NumericFormattingCondition,
];

export const defaultFormattingRule = (): NumericFormattingRule => ({
  id: CreatePrefix + generateUUID(),
  name: '',
  fallbackLogic: true,
  formattingConditions: defaultFormattingConditions() as NumericFormattingCondition[],
  type: FormattingRuleType.NUMERIC,
  fieldName: null,
});

export function isDimensionFormattingRule(rule: FormattingRule): rule is DimensionFormattingRule {
  return rule.type === FormattingRuleType.DIMENSION;
}

export function isNumericFormattingRule(rule: FormattingRule): rule is NumericFormattingRule {
  return rule.type === FormattingRuleType.NUMERIC;
}

export const measurementUnitOptions = Object.values(MeasurementUnit).map(v => generateOption(v));

export const CreatePrefix = 'create_';
export const getIsCreate = formattingRule => formattingRule?.id?.startsWith(CreatePrefix);

export const renderFormattingRuleOption = (v?: FormattingRule) =>
  v?.id && v.id !== NONE_ID ? (
    <Box sx={{ ...flex.justifyBetween, width: '100%', gap: 1 }}>
      <Box sx={ellipsis}>
        <AvTooltip>{v.name}</AvTooltip>
      </Box>
      <AvTooltip
        variant="message"
        color="white"
        hasWidthLimit={false}
        title={
          v &&
          (isDimensionFormattingRule(v) ? (
            <StringConditionColorDescription rule={v} colorSize={20} />
          ) : (
            <ColorDescription rule={v} colorSize={20} />
          ))
        }>
        <div>
          <AvItemWithLogoGroup
            items={[...new Set<string>(v.formattingConditions.map(f => f.color))]}
            variant={IconVariant.color}
            CustomItemRender={ColorItem}
            size={IconSize.collapsedXSmall}
            shouldShowTooltip={false}
            limit={3}
          />
        </div>
      </AvTooltip>
    </Box>
  ) : (
    <div>None</div>
  );

export const getFormattingRuleById = (id: string | undefined, formattingRules: FormattingRule[], formattingRuleId?: string | undefined) =>
  formattingRules.find(v => v.id === (formattingRuleId || id));

export const getColorFromFormattingConditions = ({
  value,
  formattingRule,
  defaultColor,
}: {
  value: Data;
  formattingRule?: FormattingRule;
  defaultColor?: string;
}) => {
  const formattingConditions = formattingRule?.formattingConditions || [];
  const fallbackLogic =
    formattingRule?.fallbackLogic && (formattingConditions.length > 1 || !formattingConditions?.[0]?.filter?.expression);
  const conditionWithoutLast = fallbackLogic ? formattingConditions?.slice(0, -1) : formattingConditions;
  const fallbackCondition = fallbackLogic ? formattingConditions?.at(-1) : undefined;

  return (
    (fallbackLogic ? conditionWithoutLast : formattingConditions).find(({ filter }) => {
      if (!filter) {
        return false;
      }
      if (formattingRule && isNumericFormattingRule(formattingRule)) {
        return checkValueMatchesNumericFilter(value as number, filter as NumericFilter);
      }

      if (formattingRule && isDimensionFormattingRule(formattingRule)) {
        return checkValueMatchesStringFilter(value, filter as StringFilter);
      }
      return false;
    })?.color ||
    fallbackCondition?.color ||
    defaultColor
  );
};

const checkValueMatchesStringFilter = (value: string | number | string[] | boolean | null | undefined, expressionFilter: Filter) => {
  if (Array.isArray(value)) {
    return value.toString() === expressionFilter?.expression?.stringCondition?.equals?.toString();
  }

  if (value !== null && value !== undefined) {
    return value.toString() === expressionFilter?.expression?.stringCondition?.equals?.toString();
  }

  return value === expressionFilter?.expression?.stringCondition?.equals;
};

export const checkValueMatchesNumericFilter = (value: number, expressionFilter: NumericFilter) => {
  const condition = Object.keys(expressionFilter?.expression?.numberCondition || {})?.[0] || '';
  const conditionValue = expressionFilter?.expression?.numberCondition![condition];
  switch (condition) {
    case NumberConditionType.GT: {
      return value > conditionValue;
    }
    case NumberConditionType.GTE: {
      return value >= conditionValue;
    }
    case NumberConditionType.LT: {
      return value < conditionValue;
    }
    case NumberConditionType.LTE: {
      return value <= conditionValue;
    }
    case NumberConditionType.EQUALS: {
      return value === conditionValue;
    }
    case NumberConditionType.NOT_EQUAL: {
      return value !== conditionValue;
    }
    case NumberConditionType.BETWEEN: {
      return value >= conditionValue.from && value <= conditionValue.to;
    }
    default: {
      return false;
    }
  }
};

export const hasFormattingRuleOnWidget = (widget: AllTypeWidgets) => {
  if (widget.category === WidgetCategory.Table || widget.category === WidgetCategory.Pie || widget.category === WidgetCategory.Bar) {
    return !!Object.values(widget.definition.custom.fieldStyle || {}).find(f => !!f.formattingRuleId);
  }
  if (widget.category === WidgetCategory.Tile) {
    return !!widget.definition.custom.formattingRuleId;
  }
  return false;
};

export const getFormattingRulesByType = (
  formattingRuleType: keyof typeof FormattingRuleType,
  formattingRulesOptions: FormattingRule[]
): FormattingRule[] => formattingRulesOptions.filter(({ id, type }) => id === NONE_ID || type === formattingRuleType);

const getFormattingRulesByFieldName = (dimName: string | null | undefined, formattingRulesOptions: FormattingRule[]): FormattingRule[] =>
  formattingRulesOptions.filter(({ id, fieldName }) => id === NONE_ID || fieldName === dimName);

export const filterFormattingRules = ({
  dimName,
  formattingRuleType,
  formattingRulesOptions,
}: {
  dimName: string | null | undefined;
  formattingRuleType: keyof typeof FormattingRuleType;
  formattingRulesOptions: FormattingRule[];
}): FormattingRule[] => getFormattingRulesByFieldName(dimName, getFormattingRulesByType(formattingRuleType, formattingRulesOptions));

export const getFormattingRulesByDims = ({
  dims,
  formattingRules,
}: {
  dims: Field[];
  formattingRules: FormattingRule[];
}): DimensionFormattingRule[] => {
  const selectedDimNames = new Set(dims.map(dim => dim.name));
  return formattingRules.filter(rule => selectedDimNames.has(rule.fieldName as string)) as DimensionFormattingRule[];
};

export const getColorByDimType = (dimType: string | undefined, theme: Theme, value: any, index: number) => {
  if (dimType === FormatterType.boolean) {
    if (value === true) {
      return '#38b762';
    }
    if (value === false) {
      return '#dc362e';
    }
    return '#bac2cf';
  }
  return theme.palette.chartColors[index % theme.palette.chartColors.length];
};
